import React from 'react';
import type { CreatorDeal } from '../../api/payment/brand-deal.api';
import ContentFormatBadge from './content-format-badge';
import { IndividualOrdererBadge } from './orderer-badge';
import { BrandThreadLink } from './brand-thread-panel';
import { creativeRulesLabel } from '../../utils/brand-creative';
import { BRIEF_INVITE_ASK_NOTE, briefVersionLabel, submitWindowLabel } from '../../utils/brand-brief-invite';

export interface BrandBriefInviteCardProps {
  deal: CreatorDeal;
  netLabel: string;
  respondByLabel: string;
  /** Đang có thao tác (nhận, từ chối) trên lời mời này. */
  busy: boolean;
  onAccept: () => void;
  onDecline: () => void;
}

/**
 * Lời mời làm brief đang chờ phía creator (spec 2026-10-12 §7): brief đúng bản mới nhất, định dạng, hạn nộp, luật sửa và duyệt,
 * rồi Nhận làm brief hoặc Từ chối. Không có "Đề nghị chỉnh": hỏi người đặt qua phần Trao đổi. Khai ở cấp module.
 */
const BrandBriefInviteCard: React.FC<BrandBriefInviteCardProps> = ({ deal, netLabel, respondByLabel, busy, onAccept, onDecline }) => {
  const brief = deal.brief;
  if (!brief) return null;
  const version = briefVersionLabel(brief.briefVersion);
  return (
    <div className="ui-card flex flex-col gap-3 p-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="rounded-full bg-[#EAEFFA] px-2 py-0.5 text-[12px] font-semibold text-[#0057FF] dark:bg-slate-700 dark:text-blue-300">
          Lời mời làm brief
        </span>
        <ContentFormatBadge format={deal.campaign.contentFormat} />
        {version && <span className="ui-muted text-[12px]">{version}</span>}
      </div>
      <div className="flex flex-col gap-0.5">
        <h3 className="ui-heading text-[16px] font-bold">{deal.campaign.title}</h3>
        <p className="ui-muted flex flex-wrap items-center gap-1 text-[13px]">
          {deal.campaign.brandName} <IndividualOrdererBadge kind={deal.campaign.ordererKind} />
        </p>
      </div>
      {deal.campaign.images.length > 0 && (
        <div className="flex gap-2 overflow-x-auto">
          {deal.campaign.images.map((img) => (
            <img key={img.id || img.url} src={img.url} alt="" className="size-20 shrink-0 rounded-lg object-cover" />
          ))}
        </div>
      )}
      <p className="ui-text whitespace-pre-wrap text-[14px]">{brief.briefText}</p>
      {brief.destinationUrl && (
        <a href={brief.destinationUrl} target="_blank" rel="noreferrer" className="truncate text-[13px] font-semibold text-[#0057FF]">
          {brief.destinationUrl}
        </a>
      )}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-[14px]">
        <div className="ui-text">
          Bạn nhận <span className="font-bold text-emerald-600 dark:text-emerald-400">{netLabel}</span>
        </div>
        <div className="ui-muted">{submitWindowLabel(brief.submitDays)}</div>
        <div className="ui-muted">{creativeRulesLabel(brief.maxRevisions, brief.reviewDays)}</div>
        <div className="ui-muted">Hạn trả lời: {respondByLabel}</div>
      </div>
      <p className="text-[12px] ui-muted">{brief.commitment}</p>
      <p className="text-[12px] ui-muted">{BRIEF_INVITE_ASK_NOTE}</p>
      <div className="flex flex-wrap gap-3">
        <button
          type="button"
          disabled={busy}
          onClick={onDecline}
          className="rounded-lg border ui-divider px-4 py-2 text-[14px] font-semibold ui-text disabled:opacity-60"
        >
          Từ chối
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={onAccept}
          className="rounded-lg bg-[#0057FF] px-4 py-2 text-[14px] font-semibold text-white disabled:opacity-60"
        >
          Nhận làm brief
        </button>
        <span className="self-center text-[14px]">
          <BrandThreadLink dealId={deal.id} threadId={deal.threadId} />
        </span>
      </div>
    </div>
  );
};

export default BrandBriefInviteCard;
